import { computed, inject } from '@angular/core';
import { ActivityEntityName, ApprovedPendingCode, EntityStatusType, isItemDeletionPrevented, isItemRemoved, ItemDeletedResponse, JobItemCommandId, JobSecurityRight, StageItemDetail, jobManageRights, manageDefect, type SectionRights } from '@jms/model';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { RightsService } from '@jms/utils/util.permissions';
import { injectQueryClient } from '@ngneat/query';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { type EntityStatusUpdateResult } from '@workbuddy/ui/blocks/entity.status.update';
import { injectDialog } from '@workbuddy/ui/v2/blocks/dialog';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { isPendingItemRow } from '../row/job.item.authorisation.group';
import { ItemDefectDialogComponent, ItemDefectDialogData } from '../components/item.defect.dialog';
import { ItemMediaDialogComponent, ItemMediaDialogData } from '../components/item.media.dialog';
import type { JobItemBoardParams } from '../../models/job.item.model';
import { resolveItemRecord } from '../row/item.row.record';
import { resolveApproveRefusal, resolveRequestRefusal, resolveRowStatusActions, type ItemRowStatusFacts } from './item.row.status.gates';
import { ItemStatusSetService } from '../services/item.status.set.service';
import { normalizeUpdatedAt, pickItemStatus, writeItemCancel, writeItemStatusChange } from './item.status.writes';
import { SorTaskCountService } from '../services/item.task.count.service';

/** Optional host hooks — the SOR Details page closes after delete / cancel and re-reads after Change Status; the board needs neither. */
export interface JobItemRowCommandOptions {
  onDeleted?: () => void;
  /** Re-reads a host that shows the line itself — SOR Details stays open after Change Status and would keep the old status. */
  onStatusChanged?: () => void;
  /**
   * Forces the SOR (Delete-only) menu for a caller that is SOR by construction rather than by mount
   * — the item detail page. Kept off `params` so a caller never has to fabricate a params bag it does
   * not have: every other member of {@link JobItemBoardParams} is required, and a partial one built to
   * carry this flag would satisfy the type while missing the functions this factory calls.
   */
  sorOnly?: boolean;
  /**
   * Web SOR detail opens outside the board, so the mount's section bag never arrives and Delete
   * would follow Jobs Manage alone. Kept off `params` for the same reason as {@link sorOnly}.
   * When set, it gates Edit and Delete the same way the mount bag does. Absent both, no section gate.
   */
  sectionRights?: () => SectionRights | undefined;
  /**
   * `false` leaves Request / Approve off the menu of a pending line. The SOR Details page carries its
   * own Request SOR and Approve buttons, so its ⋮ would repeat them; the SOR quick preview shows only
   * the first command, which must stay the status change. Absent = offered, as on the board.
   */
  pendingCommands?: boolean;
}

/**
 * Row ⋯ menu commands, built per row. The board resolves this factory inside
 * runInInjectionContext, which is the only reason these can reach services the manifest cannot.
 *
 * `params` come from live `EntityBoardContext.params` (JobItem board mount inputs). Without them
 * every action degrades to what the row alone can prove.
 *
 * Callers outside the board (SOR Details ⋮, SOR quick preview) pass the item's detail record with its
 * stage at `workOrders`, plus the same params, and may supply {@link JobItemRowCommandOptions.onDeleted}
 * to tear down host UI after a successful delete and {@link JobItemRowCommandOptions.onStatusChanged}
 * to re-read it after a saved Change Status.
 */
export function buildJobItemRowCommands(row: any, params?: JobItemBoardParams | null, options?: JobItemRowCommandOptions): Command[] {

  const item = resolveItemRecord(row);
  // The stage is on the row's parent level: the projection carries `workOrders._id` for exactly this.
  const stageId = row?.workOrders?._id;
  const confirmService = inject(ConfirmServicePro);
  const dialogs = injectDialog();
  const entityData = inject(EntityDataServiceRaw);
  const generalSetting = inject(GeneralSettingService);
  const rights = inject(RightsService);
  const toastService = inject(ToastService);
  const queryClient = injectQueryClient();
  const taskCounts = inject(SorTaskCountService);
  const statusSets = inject(ItemStatusSetService);

  // Warmed on the first row, read on the click: a menu is built synchronously and cannot await.
  statusSets.load();

  // Legacy read this off `jobRight.manage`, which is itself only a snapshot of the same global check
  // (`job.editor.effects.ts:46`) — so reading it here also works where there is no job store.
  const canManage = rights.hasRights(...jobManageRights);
  // Options win: the detail page opens outside the board and has no mount bag. No bag at all still means no section gate.
  const sectionRights = options?.sectionRights ?? params?.sectionRights;
  const section = sectionRights ? sectionRights() : undefined;
  const canEditLine = canManage && (!sectionRights || !!section?.edit);
  const canDeleteLine = canManage && (!sectionRights || !!section?.delete);

  // Undefined where the mount did not supply params. Each gate picks its own answer for that.
  // Optional call, not just optional access: a caller may hold a params bag assembled elsewhere, and
  // an absent member has to read as "unknown" like an absent bag — not throw and take the menu with it.
  const stageWritable = params?.stageWritable?.(stageId);

  // The board's own rows, then everything else the write moved. Invalidating the item query alone
  // leaves Compliance, Defects, Tasks, the recap totals and the job header showing pre-write values.
  const refreshItems = async (): Promise<void> => {

    // Drop the batched task/photo tally so the SOR indicators and completion cells re-fetch fresh on
    // reload — attaching a file or adding a defect changes counts the item query does not carry.
    taskCounts.clear();
    await queryClient.invalidateQueries({ queryKey: [ActivityEntityName.JobItem, 'items'], refetchType: 'all' });
    params?.itemsChanged?.();
  };

  const onAttachFile = async (): Promise<void> => {

    if (!item?._id) return;
    const data: ItemMediaDialogData = {
      itemId: item._id,
      itemName: item.name,
      stageId,
      // Legacy's `canManageMedia` was `canEdit` itself (`embedded.codes.component.ts:701`).
      // Fails closed without a host: the upload endpoint validates neither the stage nor the
      // permission, so this gate is all that stands between a finalised stage and a new file.
      // Read-only still lists and downloads what is attached.
      canManageMedia: canEditLine && (stageWritable ?? false),
    };

    // The dialog reports whether the attachment count changed. It matters: the count shows on the
    // row, and the first attachment flips the item's compliance answer, which then blocks delete.
    const changed = await dialogs.modal<boolean, ItemMediaDialogComponent>(ItemMediaDialogComponent, {
      data,
      width: 'min(1800px, calc(100vw - 40px))',
      height: 'calc(90vh - 40px)',
      maxHeight: 'calc(90vh - 40px)',
      aria: { label: 'Item attachments' },
    });

    if (changed) await refreshItems();
  };

  const onAddDefects = async (): Promise<void> => {

    if (!item?._id || !params) return;

    const data: ItemDefectDialogData = {
      item: { _id: item._id, name: item.name, location: item.location },
      job: params.job(),
      // The stage number is already on the row: legacy's `workOrder.name` was `workOrderNumber`.
      stage: { _id: stageId, name: row?.workOrders?.workOrderNumber },
      dateFormat: generalSetting.generalSettings?.region?.dateFormat,
      timezone: params.timezone(),
      currentUser: params.currentUser(),
    };

    // No fixed height: the form is short, and forcing one leaves the footer floating above dead space.
    const saved = await dialogs.modal<boolean, ItemDefectDialogComponent>(ItemDefectDialogComponent, {
      data,
      width: 'min(560px, calc(100vw - 40px))',
      maxHeight: 'calc(100vh - 80px)',
      aria: { label: 'New defect' },
    });

    if (saved) await refreshItems();
  };

  // Picks the status set the dialog offers; a wrong answer here offers reasons the server cannot match.
  // Board rows carry the `authorisation` stamp; the SOR Details record carries `isClaimCode` instead.
  const isPending = item?.isClaimCode === true || isPendingItemRow(item);

  const onApprove = async (): Promise<void> => {

    if (!item?._id) return;

    let approved: ApprovedPendingCode[] | undefined;
    try {
      approved = await lastValueFrom(entityData.executeCommand<ApprovedPendingCode[]>(ActivityEntityName.JobItem, JobItemCommandId.ApproveItem, { itemId: item._id }));
    } catch (httpError) {
      toastService.error('Cannot approve item', httpError?.error?.message ?? undefined);
      return;
    }

    await refreshItems();
    const taskLeftOpen = approved?.find(code => code._id === item._id)?.taskLeftOpen;
    if (taskLeftOpen) toastService.warning('Item approved', taskLeftOpen);
    else toastService.success('Item approved', item.name ?? 'this item');
  };

  const onRequest = async (): Promise<void> => {

    if (!item?._id) return;

    // The server names the status, so a tenant that asks for a reason on Requested refuses this; the
    // day it does, this is where the dialog goes.
    try {
      await lastValueFrom(entityData.executeCommand(ActivityEntityName.JobItem, JobItemCommandId.RequestItem, { itemId: item._id }));
    } catch (httpError) {
      toastService.error('Cannot request item', httpError?.error?.message ?? undefined);
      return;
    }

    await refreshItems();
    toastService.success('Item requested', item.name ?? 'this item');
  };

  const statusSetType = isPending ? ActivityEntityName.ItemPending : ActivityEntityName.Item;

  // A tenant that prevents deletion is offered Cancel in Delete's place, so the item and its tasks, defects and finance lines survive.
  // A Draft keeps Delete (WBS-12205). Only the set says which status is Draft, so this waits for it as the status actions do.
  const $deletionPrevented = computed(() => {

    const current = statusSets.snapshot(statusSetType)?.find(status => status._id === item?.itemStatus?._id);

    return isItemDeletionPrevented(generalSetting.generalSettings, current && { ...current, entityType: statusSetType });
  });

  // The status dialog in the middle of the screen, offering only the line's own set: a status from the
  // other set is one the server refuses for this line.
  const pickStatus = (title: string, displayType: EntityStatusType): Promise<EntityStatusUpdateResult | undefined> => pickItemStatus(dialogs, { title, entityType: statusSetType, displayType, status: item?.itemStatus });

  // A cancel asks where to and why; a delete has nothing to ask, because the line is going.
  const onCancel = async (): Promise<void> => {

    if (!item?._id) return;
    const name = item.name ?? 'this item';
    const chosen = await pickStatus(`Cancel ${name}`, EntityStatusType.Inactive);
    if (!chosen) return;

    try {
      await writeItemCancel(entityData, item._id, chosen);
    } catch (httpError) {
      toastService.error('Cannot cancel item', httpError?.error?.message ?? undefined);
      return;
    }

    await refreshItems();

    toastService.success('Item cancelled', name);
    options?.onDeleted?.();
  };

  // Moves the line within the Active statuses of its set; leaving them is Cancel's job.
  const onChangeStatus = async (): Promise<void> => {

    if (!item?._id) return;
    const name = item.name ?? 'this item';
    const chosen = await pickStatus(`Change status of ${name}`, EntityStatusType.Active);
    if (!chosen) return;

    try {
      await writeItemStatusChange(entityData, item._id, chosen);
    } catch (httpError) {
      toastService.error('Cannot change item status', httpError?.error?.message ?? undefined);
      return;
    }

    await refreshItems();
    toastService.success('Item status changed', `${name} → ${chosen.name}`);
    options?.onStatusChanged?.();
  };

  const onDelete = async (): Promise<void> => {

    if (!item?._id) return;
    const name = item.name ?? 'this item';

    const confirmed = await confirmService.confirm('Delete item', `Delete ${name}? Any tasks or defects on the item go with it.`, 'danger');
    if (!confirmed) return;

    let result: ItemDeletedResponse;

    // Two different refusals reach here. A compliance answer or an approved invoice/bill line comes
    // back as a body saying it failed; a security conflict rejects the call outright. Both have to
    // surface, or the row simply stays put and the click looks like it did nothing.
    try {
      const detail = await firstValueFrom(entityData.getById<StageItemDetail>(ActivityEntityName.JobItem, item._id));
      const updatedAt = normalizeUpdatedAt(detail?.item?.stage?.updatedAt);
      if (!updatedAt) {
        toastService.error('Cannot delete item', 'The current stage updatedAt is required. Refresh the Job and try again.');
        return;
      }
      result = await lastValueFrom(entityData.executeCommand<ItemDeletedResponse>(ActivityEntityName.JobItem, JobItemCommandId.DeleteItem, { itemId: item._id, updatedAt }));
    } catch (httpError) {

      toastService.error('Cannot delete item', httpError?.error?.message ?? undefined);
      return;
    }

    if (result && result.success === false) {
      toastService.error('Cannot delete item', result.error ?? undefined);
      return;
    }

    await refreshItems();

    toastService.success('Item deleted', name);
    options?.onDeleted?.();
  };

  // Legacy omitted this rather than disabling it, on `item.canEdit && jobRight.manageDefect`
  // (`embedded.codes.table.component.ts:766`). `canEdit` reduces to the two terms below: all three
  // branches of `updateCodePermission` return it unchanged (`embedded.codes.component.ts:717-729`).
  // Dropped is its one path without the manage right — a price field left editable by `modifyPricing`
  // alone (`:757`) — which offered defects on a stage the user cannot manage.
  // Fails closed without a host, since the defect write does not validate the stage either.
  const canAddDefects = canManage && (stageWritable ?? false) && rights.hasRights(...manageDefect);

  // State reasons disable and explain; a missing right hides the command instead (see `canManage`
  // below). `invoiceApproved` and `billApproved` are stamped onto the row by the items read, so Delete
  // can refuse up front as the legacy Items tab did. `claimImport` is still not on the row, and
  // `resolveCodeCapability` reads three more the projection drops, so it would answer `true` for every
  // row. Those stay with the server, which re-reads invoice and bill status live
  // (`workorder.delete.code.validation.task.ts:50-92`).
  //
  // Unlike media, an unprovable stage stays enabled: the command asserts the same rule
  // (`stage.item.security.ts:48-62`), so the worst case is a refusal that explains itself. Pending
  // items too — the command refuses all of them today, but that is under review and a client gate
  // would keep them blocked after the server relaxes.
  // Same predicate the server and the task lifecycle use, not a restatement of it: while this had its
  // own copy it read a narrower rule than `isItemRemoved`, so a line already treated as removed still
  // offered an enabled Cancel whose only feedback was the refusal toast.
  const alreadyCancelled = isItemRemoved(item, isPending);

  const deleteBlockedBy = stageWritable === false
    ? 'The stage is closed or finalised.'
    : item?.purchaseOrder?._id
      ? 'The item is on a purchase order.'
      : null;
  const statusChangeBlockedBy = stageWritable === false ? 'The stage is closed or finalised.' : null;

  // Delete only: the server's delete validation refuses a line on an approved invoice or bill, while
  // Cancel is a status move the server does not gate on finance documents.
  const financeBlockedBy = item?.invoiceApproved === true
    ? 'The item is on an approved invoice.'
    : item?.billApproved === true
      ? 'The item is on an approved bill.'
      : null;

  const statusFacts: ItemRowStatusFacts = { stageWritable, statusSet: statusSets.snapshot(), statusId: item?.itemStatus?._id, systemName: item?.itemStatus?.systemName, alreadyCancelled };
  const approveBlockedBy = resolveApproveRefusal(statusFacts);
  const requestBlockedBy = resolveRequestRefusal(statusFacts);

  // Both status actions follow the section's Edit and the status's own "Can be edited by"; Cancel also
  // needs an Active status and, on the Items tab, keeps standing in for Delete while the tenant prevents deletion.
  // A `computed` over the set, because the board builds this menu once, when the row first renders — often
  // before the sets arrive. Read eagerly, a locked line kept the actions it was first given.
  const sorOnly = options?.sorOnly ?? params?.sorOnly?.() === true;
  const $statusActions = computed(() => resolveRowStatusActions({
    isPending,
    canEditLine,
    deletionPrevented: $deletionPrevented(),
    sorOnly,
    statusSet: statusSets.snapshot(statusSetType),
    statusId: item?.itemStatus?._id,
    statusType: item?.itemStatus?.statusType,
    raisedBy: item?.raisedBy,
  }));

  // A right the user does not have hides the command; only a state they could get out of disables it.
  // A disabled control tells someone who will never hold the right that the action is theirs to earn.
  //
  // SOR row ⋮ (board + detail) carries the status actions and Delete only — Attach File and Add Defects
  // live elsewhere on that surface (evidence rail / indicators). Items Pro keeps the full menu.
  // Change Status sits above Cancel; Delete stays where it was when the tenant allows deletion.
  const statusCommands = canEditLine
    ? [
      new Command({ label: 'Change Status', action: onChangeStatus, hidden: computed(() => !$statusActions().changeStatus), disabled: !!statusChangeBlockedBy, tooltip: statusChangeBlockedBy ?? undefined }),
      new Command({ label: 'Cancel', action: onCancel, hidden: computed(() => !$statusActions().cancel), disabled: !!deleteBlockedBy, tooltip: deleteBlockedBy ?? undefined }),
    ]
    : [];
  const deleteCommand = canDeleteLine
    ? [new Command({ label: 'Delete', action: onDelete, hidden: computed(() => $deletionPrevented()), disabled: !!(deleteBlockedBy ?? financeBlockedBy), tooltip: deleteBlockedBy ?? financeBlockedBy ?? undefined })]
    : [];


  // Pending only: an authorised line has nothing to request and is past approving. Both move the
  // line's status, so they need the section's Edit.
  const pendingCommands = !isPending || !canEditLine || options?.pendingCommands === false
    ? []
    : [
      new Command({ label: sorOnly ? 'Request SOR' : 'Request', action: onRequest, disabled: !!requestBlockedBy, tooltip: requestBlockedBy ?? undefined }),
      ...(rights.hasRights(JobSecurityRight.ApprovePendingItems)
        ? [new Command({ label: 'Approve', action: onApprove, disabled: !!approveBlockedBy, tooltip: approveBlockedBy ?? undefined })]
        : []),
    ];

  if (sorOnly) {
    return [...pendingCommands, ...statusCommands, ...deleteCommand];
  }

  return [
    new Command({ label: 'Attach File', action: onAttachFile }),
    ...(canAddDefects ? [new Command({ label: 'Add Defects', action: onAddDefects })] : []),
    ...pendingCommands,
    ...statusCommands,
    ...deleteCommand,
  ];
}
